import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzUploadChangeParam, NzUploadFile } from 'ng-zorro-antd/upload';

@Component({
  selector: 'app-upload-icon-popup',
  templateUrl: './upload-icon-popup.component.html',
  styleUrls: ['./upload-icon-popup.component.scss'],
  standalone: false
})
export class UploadIconPopupComponent implements OnInit {
  readonly nzModalData: any = inject(NZ_MODAL_DATA);
  data: any;
  selectedImgKey!: string;
  selectedImgBase64: string | undefined;
  selectedImgDataUrl: string | undefined;
  uploadedFile: File | null = null;
  fileError: string = '';
  showPreviewModal: boolean = false;
  iconFileName: string = '';
  iconFileUpload: boolean = false;
  iconProgress: number = 0;
  isIconUploading: boolean = false;
  iconFileList: NzUploadFile[] = [];

  processIcons: any = [
    {
      key: 'lockIcon',
      image: '../../../../assets/images/createProcessIcon/lockIcon.svg',
    },
    {
      key: 'cartIcon',
      image: '../../../../assets/images/createProcessIcon/cartIcon.svg',
    },
    {
      key: 'bankIcon',
      image: '../../../../assets/images/createProcessIcon/bankIcon.svg',
    },
    {
      key: 'boxOpen',
      image: '../../../../assets/images/createProcessIcon/boxOpen.svg',
    },
    {
      key: 'folder',
      image: '../../../../assets/images/createProcessIcon/folder.svg',
    },
    {
      key: 'code',
      image: '../../../../assets/images/createProcessIcon/code.svg',
    },
    {
      key: 'money-bag',
      image: '../../../../assets/images/createProcessIcon/money-bag.svg',
    },
    {
      key: 'factory',
      image: '../../../../assets/images/createProcessIcon/factory.svg',
    },
    {
      key: 'home',
      image: '../../../../assets/images/createProcessIcon/home.svg',
    },
    {
      key: 'addressBook',
      image: '../../../../assets/images/createProcessIcon/addressBook.svg',
    },
    {
      key: 'Medkit',
      image: '../../../../assets/images/createProcessIcon/Medkit.svg',
    },
    {
      key: 'ball',
      image: '../../../../assets/images/createProcessIcon/ball.svg',
    },
    {
      key: 'graph',
      image: '../../../../assets/images/createProcessIcon/graph.svg',
    },
    {
      key: 'mapMarker',
      image: '../../../../assets/images/createProcessIcon/mapMarker.svg',
    },
    {
      key: 'briefCase',
      image: '../../../../assets/images/createProcessIcon/briefCase.svg',
    },
    {
      key: 'plant',
      image: '../../../../assets/images/createProcessIcon/plant.svg',
    },
    {
      key: 'default_process',
      image: '../../../../assets/images/createProcessIcon/default_process.svg',
    },
    {
      key: 'supply_finance',
      image: '../../../../assets/images/createProcessIcon/supply_finance.svg',
    },
  ];

  constructor(
    private modalRef: NzModalRef,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.data = this.nzModalData?.data;
    // Pre-select existing icon when editing
    if (this.data?.selectedIconBase64) {
      this.selectedImgBase64 = this.data.selectedIconBase64;
      const mime = this.detectMimeFromBase64(this.data.selectedIconBase64);
      this.selectedImgDataUrl = 'data:' + mime + ';base64,' + this.data.selectedIconBase64;
      this.selectedImgKey = this.data.selectedIconName || '';
    }
  }

  private detectMimeFromBase64(b64: string): string {
    if (!b64) return 'image/svg+xml';
    // PNG signature starts with iVBORw0KGgo
    if (b64.startsWith('iVBOR')) return 'image/png';
    // JPEG signatures: /9j/ (JFIF/Exif)
    if (b64.startsWith('/9j/')) return 'image/jpeg';
    // Default to SVG
    return 'image/svg+xml';
  }

  selectedProcessIcon(data: any) {
    // Allow selecting a predefined icon even if an uploaded file exists
    this.uploadedFile = null;
    this.iconFileName = '';
    this.selectedImgKey = data.key;
    // Show preview immediately using asset URL
    this.selectedImgDataUrl = data.image;
    // Also prepare base64 payload for saving
    this.loadImageAsBase64(data.image);
    this.fileError = '';
    // Reset file input so user can re-upload if desired
    const fileInput = document.getElementById('iconUpload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  loadImageAsBase64(imageUrl: string) {    
    fetch(imageUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.arrayBuffer();
      })
      .then((buffer) => {
        const base64 = this.arrayBufferToBase64(buffer);
        this.selectedImgBase64 = base64;
        this.selectedImgDataUrl = 'data:image/svg+xml;base64,' + base64;
      })
      .catch((error) => {
        console.error('Error loading image:', error);
      });
  }

  arrayBufferToBase64(buffer: ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private encodeSvgTextToBase64(svgText: string): string {
    // Handle UTF-8 characters safely before base64-encoding
    return btoa(unescape(encodeURIComponent(svgText)));
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this.fileError = '';
    this.selectedImgKey = ''; // Clear predefined icon selection

    // Check file type (some browsers may not set type for SVGs, so fallback to extension)
    const isSvgType = file.type && file.type.includes('svg');
    const isSvgExt = (file.name || '').toLowerCase().endsWith('.svg');
    if (!(isSvgType || isSvgExt)) {
      this.fileError = 'Only SVG files are allowed';
      return;
    }

    // Check file size (100KB = 102400 bytes)
    if (file.size > 102400) {
      this.fileError = 'File size must be less than 100KB';
      return;
    }

    // Read file and check dimensions
    const reader = new FileReader();
    reader.onload = (e) => {
      const svgContent = e.target?.result as string;
      this.checkSvgDimensions(svgContent, file);
    };
    reader.readAsText(file);
  }

  checkSvgDimensions(svgContent: string, file: File) {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElement = svgDoc.querySelector('svg');
    
    if (!svgElement) {
      this.fileError = 'Invalid SVG file';
      return;
    }

    const width = svgElement.getAttribute('width');
    const height = svgElement.getAttribute('height');
    const viewBox = svgElement.getAttribute('viewBox');

    let svgWidth = 0;
    let svgHeight = 0;

    if (width && height) {
      svgWidth = parseFloat(width);
      svgHeight = parseFloat(height);
    } else if (viewBox) {
      const viewBoxValues = viewBox.split(' ');
      svgWidth = parseFloat(viewBoxValues[2]);
      svgHeight = parseFloat(viewBoxValues[3]);
    }

    if (svgWidth > 25 || svgHeight > 25) {
      this.fileError = 'Image dimensions must be 25x25 or smaller';
      return;
    } else {
      this.processUploadedFile(file);
    }
  }

  processUploadedFile(file: File) {
    // Generate data URL for display
    const dataReader = new FileReader();
    dataReader.onload = (ev) => {
      this.selectedImgDataUrl = ev.target?.result as string;
      this.cdr.markForCheck();
    };
    dataReader.readAsDataURL(file);

    // Generate base64 payload from raw SVG text (UTF-8 safe)
    const textReader = new FileReader();
    textReader.onload = (e) => {
      const svgContent = e.target?.result as string;
      const base64 = this.encodeSvgTextToBase64(svgContent);
      this.selectedImgBase64 = base64;
      this.uploadedFile = file;
      this.fileError = '';
      this.cdr.markForCheck();
    };
    textReader.readAsText(file);
  }

  // preview modal removed; showing inline

  getIconContainerClass(item: any): string {
    return this.selectedImgKey === item.key ? 'greenColor' : 'lb-bgcolor';
  }

  removeUploadedIcon() {
    // Clear all icon data to show drag and drop area again
    this.uploadedFile = null;
    this.iconFileName = '';
    this.selectedImgBase64 = undefined;
    this.selectedImgDataUrl = undefined;
    this.selectedImgKey = '';
    this.fileError = '';
    
    // Reset file input
    const fileInput = document.getElementById('iconUpload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  cancel() {
    this.modalRef.destroy();
  }

  save() {
    const data = {
      icon: this.selectedImgBase64 || '',
      isUploaded: !!this.uploadedFile,
      fileName: this.uploadedFile?.name || this.selectedImgKey || ''
    };
    this.modalRef.destroy(data);
  }

  // Drag and drop upload methods
  beforeIconUpload = (file: NzUploadFile): boolean => {
    // Validate file type
    if (!file.name?.toLowerCase().endsWith('.svg')) {
      this.fileError = 'Only SVG files are allowed';
      return false;
    }

    // Validate file size (100kb = 100 * 1024 bytes)
    if (file.size && file.size > 100 * 1024) {
      this.fileError = 'File size must be less than 100KB';
      return false;
    }

    this.iconFileName = file.name;
    this.uploadedFile = file as any;
    this.fileError = '';

    // Generate data URL for immediate preview
    const dataReader = new FileReader();
    dataReader.onload = (ev) => {
      this.selectedImgDataUrl = ev.target?.result as string;
      this.cdr.markForCheck();
    };
    dataReader.readAsDataURL(file as any);

    // Generate base64 payload
    this.convertIconToBase64(file).then((res) => {
      const base64 = res?.split(',')?.[1];
      this.selectedImgBase64 = base64;
      this.selectedImgKey = file.name?.replace('.svg', '') || 'Custom Icon';
    });

    return false;
  };

  convertIconToBase64(file: NzUploadFile): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file as any);
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject('Error converting file to base64: ' + error);
      };
    });
  }


  uploadIcon() {
    this.isIconUploading = true;
    // Simulate upload process
    setTimeout(() => {
      this.isIconUploading = false;
      this.save(); // Call the existing save method
    }, 1000);
  }

  handleIconChange({ file }: NzUploadChangeParam): void {
    this.iconFileName = file.name;
  }
}
